using "./dom";
using "./velement";

// The diff/patch engine behind real vdom diffing: Component.Update() (see
// component.ks) calls Patch() with the PREVIOUS render's VElement tree
// (which carries each node's real, live DOM counterpart via its own
// RealNode field) and the NEW tree Render() just produced, and gets back
// real DOM mutated/reused in place wherever possible instead of a full
// subtree rebuild.
//
// Deliberately never reads the live DOM back to rediscover structure (no
// "get current children"/"get current tag" API exists, or is needed) —
// the retained *previous* VElement tree already records everything Patch()
// needs to know about what's currently there. This is what makes the whole
// approach work without a generic children/attributes read-back API that
// KopScript's narrow, curated DOM binding doesn't have.

// A component-like thing Batching (below) can flush once every DOM-event-
// triggered Update() call inside one batch has registered itself. Declared
// HERE, not in component.ks, specifically so THIS file's own Materialize/
// Patch — which need to start/stop a batch around every real event
// dispatch — never have to `using "./component"`: component.ks already
// `using`s this file for Materialize/Patch themselves, and KopScript
// rejects a circular `using` outright. Component (component.ks) is the
// one real implementer, via `class Component : Flushable`.
interface Flushable {
  void FlushUpdate();
}

// Coalesces every Update() call made while a real DOM event handler
// Kopular itself attached (see Materialize/Patch below, both of which wrap
// each listener in Batching.Run before attaching it) into one flush per
// affected component, applied once that handler returns — not one flush
// per state<T> write inside it. See Component.Update()'s own comment
// (component.ks) for the full rationale; this is just the mechanism.
//
// Deliberately class-level, shared across every batch/component rather
// than per-instance — a single click can trigger Update() on more than one
// component (e.g. a shared service's state<T> notifying two sibling
// pages), and all of them need to flush together, once, when that one
// handler finishes.
class Batching {
  private static number Depth = 0;
  private static Flushable[] Pending = [];

  public static bool IsActive() {
    return Batching.Depth > 0;
  }

  public static void Defer(Flushable f) {
    if (!Batching.Pending.Includes(f)) {
      Batching.Pending = Batching.Pending.Push(f);
    }
  }

  // `try`/`finally`, not a bare sequence: a handler that throws must still
  // decrement Depth and flush whatever already-triggered renders are
  // pending, or one uncaught exception would wedge every future click/
  // input on the page into "always batching, never rendering." The
  // exception itself still propagates unchanged — this never catches it,
  // only guarantees the cleanup runs.
  public static void Run(() => void action) {
    Batching.Depth = Batching.Depth + 1;
    try {
      action();
    } finally {
      Batching.Depth = Batching.Depth - 1;
      if (Batching.Depth == 0) {
        Flushable[] toFlush = Batching.Pending;
        Batching.Pending = [];
        foreach (Flushable f in toFlush) {
          f.FlushUpdate();
        }
      }
    }
  }
}

// The runtime half of kopscript's `styles from "<path>.css";` (see its own
// README/LLM.md "Templates" section) — the compiler rewrites a class's own
// stylesheet at compile time (every selector scoped to that class's
// `data-kop-scope="<id>"` attribute) and splices exactly one
// `ScopedStyles.Inject(id, css);` call into its constructor. Same
// static-array-registry-with-Includes-dedup shape as Batching above,
// deliberately: dedup here is per component TYPE, not per instance — every
// instance's constructor calls Inject with the same `id`/`css` (both
// compile-time constants for that class), so only the first one actually
// creates a <style> tag; the rest are no-ops. Never removed once injected
// — a scoped stylesheet is global infrastructure for as long as the page
// lives, not per-instance content Teardown() would ever need to clean up.
class ScopedStyles {
  private static string[] Injected = [];

  public static void Inject(string scopeId, string css) {
    if (!ScopedStyles.Injected.Includes(scopeId)) {
      ScopedStyles.Injected = ScopedStyles.Injected.Push(scopeId);
      Element style = document.createElement("style");
      style.setAttribute("data-kop-scope-sheet", scopeId);
      style.textContent = css;
      document.head.appendChild(style);
    }
  }
}

// Builds a brand-new, fully real DOM subtree from a VElement tree with no
// diffing at all — first mount, or whenever Patch() decides a subtree must
// be replaced outright (no previous node to reuse, or the tag changed).
// Mutates `tree.RealNode` (and recursively every descendant's) as a side
// effect, so the tree this was called on becomes the new "previous tree"
// the next Patch() call diffs against. Takes `parent` — every call site
// already knows it (either Patch's own `parent` parameter, or the real
// element a recursive child call is about to be appendChild'd into) —
// solely to hand to a Mounted slot's own MountAsChild, which needs to
// remember its real parent for THAT component's own future self-triggered
// re-renders; an ordinary (non-Mounted) VElement never uses it.
Element Materialize(VElement tree, Element parent) {
  Mountable? maybeMounted = tree.Mounted;
  if (maybeMounted != null) {
    Mountable m = maybeMounted;
    Element mountedRoot = m.MountAsChild(parent);
    tree.RealNode = mountedRoot;
    return mountedRoot;
  }

  Element el = document.createElement(tree.Tag);

  if (tree.RawHtml.Length > 0) {
    el.innerHTML = tree.RawHtml;
  } else if (tree.Children.Length > 0) {
    foreach (VElement child in tree.Children) {
      el.appendChild(Materialize(child, el));
    }
  } else {
    el.textContent = tree.TextContent;
  }

  el.className = tree.ClassName;
  el.id = tree.Id;
  el.value = tree.Value;

  for (number i = 0; i < tree.ExtraNames.Length; i = i + 1) {
    el.setAttribute(tree.ExtraNames[i], tree.ExtraValues[i]);
  }
  if (tree.Disabled) {
    el.disabled = true;
  }
  if (tree.Checked) {
    el.checked = true;
  }

  // Skip attaching VElement's own shared no-op (see velement.ks) — it does
  // nothing when invoked, so registering it costs real work (a listener
  // list entry, held onto for nothing) for zero benefit. A real handler
  // (never equal to the shared no-op) always gets attached as before —
  // wrapped in Batching.Run so every Update() it triggers coalesces with
  // any others from the same dispatch (see Component.Update()'s own
  // comment). The wrapper itself, not tree.OnClick, is what actually gets
  // registered — recorded on tree.AttachedOnClick (velement.ks) so a later
  // Patch() can remove this exact reference, not the handler value itself.
  if (tree.OnClick != NoOpEventHandler) {
    (Event) => void wrapped = (Event e) => Batching.Run(() => tree.OnClick(e));
    tree.AttachedOnClick = wrapped;
    el.addEventListener("click", wrapped);
  }
  if (tree.OnInput != NoOpEventHandler) {
    (Event) => void wrapped = (Event e) => Batching.Run(() => tree.OnInput(e));
    tree.AttachedOnInput = wrapped;
    el.addEventListener("input", wrapped);
  }
  if (tree.OnBlur != NoOpEventHandler) {
    (Event) => void wrapped = (Event e) => Batching.Run(() => tree.OnBlur(e));
    tree.AttachedOnBlur = wrapped;
    el.addEventListener("blur", wrapped);
  }
  if (tree.OnChange != NoOpEventHandler) {
    (Event) => void wrapped = (Event e) => Batching.Run(() => tree.OnChange(e));
    tree.AttachedOnChange = wrapped;
    el.addEventListener("change", wrapped);
  }

  tree.RealNode = el;
  return el;
}

// Diffs `updated` against `old` (the previous render's tree for this exact
// position, or null if there is none — first mount) and returns the real
// DOM node now representing `updated`, reusing `old`'s real node in place
// whenever the tag matches. `parent` is only used to attach/replace at the
// top of whatever subtree Patch() is called on — child-level attach/replace
// happens inside PatchChildren.
//
// Deliberately all positive-branch `if (x != null) { ... } else { ... }`,
// never an early-return guard clause — KopScript's nullable narrowing is
// scope-based, not reachability-based, so `if (x == null) { return; }
// use(x);` would NOT narrow `x` afterward (see KopScript's own LLM.md "Common
// mistakes"). Every nullable member-access path (`oldTree.RealNode`,
// never narrows directly either) is read into a local first for the same
// reason.
Element Patch(Element parent, VElement? old, VElement updated) {
  Mountable? newMounted = updated.Mounted;
  Mountable? oldMounted = null;
  if (old != null) {
    VElement oldTreeForMount = old;
    oldMounted = oldTreeForMount.Mounted;
  }

  // A live child slot (VElement.Mounted — see velement.ks) is handled
  // entirely separately from the ordinary Tag-based logic below: it's not
  // Kopular's own DOM element to create/reuse at all, and its "same node
  // or replace" decision is instance identity (== on the Mountable itself,
  // real reference equality — interfaces erase to the underlying object),
  // not Tag equality.
  if (newMounted != null) {
    Mountable nm = newMounted;
    if (oldMounted != null) {
      Mountable om = oldMounted;
      if (om == nm) {
        // Same instance still in this slot: patch it in place, don't touch
        // the DOM position at all (its own real node, reused or not, is
        // already exactly where it needs to be).
        Element reused = nm.PatchAsChild();
        updated.RealNode = reused;
        return reused;
      }
    }
    // First time this slot has anything mounted, or a DIFFERENT instance
    // took over the slot: release whatever was here, then mount fresh.
    UnmountPrevious(parent, old, oldMounted);
    Element created = nm.MountAsChild(parent);
    parent.appendChild(created);
    updated.RealNode = created;
    return created;
  }

  if (oldMounted != null) {
    // Slot reverted from a live component back to plain content: release
    // the old one, then fall through to an ordinary fresh materialize.
    UnmountPrevious(parent, old, oldMounted);
    Element created = Materialize(updated, parent);
    parent.appendChild(created);
    return created;
  }

  if (old != null) {
    VElement oldTree = old;
    Element? maybeOldNode = oldTree.RealNode;
    if (maybeOldNode != null) {
      Element realNode = maybeOldNode;
      if (oldTree.Tag != updated.Tag) {
        Element created = Materialize(updated, parent);
        parent.replaceChild(created, realNode);
        return created;
      } else {
        updated.RealNode = realNode;

        if (updated.RawHtml.Length > 0 || oldTree.RawHtml.Length > 0) {
          if (updated.RawHtml != oldTree.RawHtml) {
            realNode.innerHTML = updated.RawHtml;
          }
        } else if (updated.Children.Length > 0) {
          // Children own this node's content now. If the old tree was a
          // plain text leaf (no tracked children of its own), any raw text
          // Materialize/a previous Patch left directly in the real node is
          // untracked by PatchChildren (which only knows about VElement-
          // backed children) — clear it first, or the new children would
          // just be appended alongside stray leftover text.
          if (oldTree.Children.Length == 0) {
            realNode.textContent = "";
          }
          PatchChildren(realNode, oldTree.Children, updated.Children);
        } else {
          // updated is a plain text leaf (or empty). Any of oldTree's own
          // real children need tearing down/removing properly first
          // (nested Mounted components included, via PatchChildren's own
          // Pass 4) — textContent's native browser side effect would
          // otherwise silently detach them without running their own
          // OnUnmount(), and a later PatchChildren call against those now-
          // orphaned nodes would throw trying to remove an already-detached
          // child.
          if (oldTree.Children.Length > 0) {
            PatchChildren(realNode, oldTree.Children, []);
          }
          if (updated.TextContent != oldTree.TextContent) {
            realNode.textContent = updated.TextContent;
          }
        }

        if (updated.ClassName != oldTree.ClassName) {
          realNode.className = updated.ClassName;
        }
        if (updated.Id != oldTree.Id) {
          realNode.id = updated.Id;
        }
        // Always assigned, never conditionally on updated.Value != oldTree.Value
        // — unlike TextContent/ClassName/Id, an <input>/<select>'s live value
        // can diverge from the last-recorded VElement.Value purely through
        // user interaction (typing, picking an option) with no Update() ever
        // running in between (a real, deliberate pattern — see KopularDemo's
        // dogs_page.ks, which never Update()s on input/change). The recorded
        // oldTree.Value only reflects the tree as of the last actual render,
        // so comparing against it can't tell "genuinely unchanged" apart from
        // "changed live in the DOM since then, framework never told" — the
        // same reason a real "controlled input" (React's own term for this)
        // always writes value on every render rather than diffing it.
        realNode.value = updated.Value;

        // Same-length is the overwhelmingly common case (the same Render()
        // code path calls SetAttr the same number of times, in the same
        // order, on every call) — compare aligned by index and only touch
        // the real DOM for an entry that actually changed, rather than
        // reapplying every extra attribute on every patch regardless. A
        // length mismatch (the rarer case: a SetAttr call was added,
        // removed, or made conditional between renders) falls back to
        // reapplying everything, since index-aligned comparison isn't
        // meaningful once the two arrays don't correspond entry-for-entry.
        if (updated.ExtraNames.Length == oldTree.ExtraNames.Length) {
          for (number i = 0; i < updated.ExtraNames.Length; i = i + 1) {
            if (updated.ExtraNames[i] != oldTree.ExtraNames[i] || updated.ExtraValues[i] != oldTree.ExtraValues[i]) {
              realNode.setAttribute(updated.ExtraNames[i], updated.ExtraValues[i]);
            }
          }
        } else {
          for (number i = 0; i < updated.ExtraNames.Length; i = i + 1) {
            realNode.setAttribute(updated.ExtraNames[i], updated.ExtraValues[i]);
          }
        }
        foreach (string oldName in oldTree.ExtraNames) {
          if (!updated.ExtraNames.Includes(oldName)) {
            realNode.removeAttribute(oldName);
          }
        }

        if (updated.Disabled != oldTree.Disabled) {
          realNode.disabled = updated.Disabled;
        }
        // Like Value above, a checkbox's live checked state changes on click
        // with no render in between, so a checked one is re-asserted every
        // patch rather than diffed against the previous tree.
        if (updated.Checked || oldTree.Checked) {
          realNode.checked = updated.Checked;
        }

        // Swap a listener only when the handler reference actually changed.
        // A node that never sets a real OnClick/OnInput/OnBlur/OnChange
        // keeps VElement's own shared NoOpEventHandler reference on both
        // sides (see velement.ks) — comparing by `!=` costs nothing and
        // skips two real DOM API calls per event per node for the (common)
        // case of "this node has no handler of this kind either time,"
        // which matters a lot on a list with hundreds/thousands of rows
        // most of which set at most one or two of the four. A node WITH a
        // real handler still gets a fresh closure every render (it
        // captures per-render values, like a loop's own item), so it still
        // swaps every time — correctly, since the old closure really is
        // stale.
        //
        // The actually-attached listener is always a Batching.Run wrapper
        // (see Materialize above), never `oldTree.OnClick`/`updated.OnClick`
        // themselves — removeEventListener only works when passed the exact
        // reference addEventListener received, so removal always goes
        // through `oldTree.AttachedOnClick` (the wrapper Materialize/a
        // previous Patch actually registered), and a real swap builds a
        // fresh wrapper the same way Materialize does. When nothing swaps,
        // `updated.AttachedOnClick` still needs to carry `oldTree`'s
        // forward — `updated` is a brand-new VElement whose own
        // AttachedOnClick starts back at the shared no-op (velement.ks's
        // constructor), and it becomes the retained "old tree" the very
        // next Patch() call diffs against.
        if (updated.OnClick != oldTree.OnClick) {
          realNode.removeEventListener("click", oldTree.AttachedOnClick);
          (Event) => void wrappedClick = (Event e) => Batching.Run(() => updated.OnClick(e));
          updated.AttachedOnClick = wrappedClick;
          realNode.addEventListener("click", wrappedClick);
        } else {
          updated.AttachedOnClick = oldTree.AttachedOnClick;
        }
        if (updated.OnInput != oldTree.OnInput) {
          realNode.removeEventListener("input", oldTree.AttachedOnInput);
          (Event) => void wrappedInput = (Event e) => Batching.Run(() => updated.OnInput(e));
          updated.AttachedOnInput = wrappedInput;
          realNode.addEventListener("input", wrappedInput);
        } else {
          updated.AttachedOnInput = oldTree.AttachedOnInput;
        }
        if (updated.OnBlur != oldTree.OnBlur) {
          realNode.removeEventListener("blur", oldTree.AttachedOnBlur);
          (Event) => void wrappedBlur = (Event e) => Batching.Run(() => updated.OnBlur(e));
          updated.AttachedOnBlur = wrappedBlur;
          realNode.addEventListener("blur", wrappedBlur);
        } else {
          updated.AttachedOnBlur = oldTree.AttachedOnBlur;
        }
        if (updated.OnChange != oldTree.OnChange) {
          realNode.removeEventListener("change", oldTree.AttachedOnChange);
          (Event) => void wrappedChange = (Event e) => Batching.Run(() => updated.OnChange(e));
          updated.AttachedOnChange = wrappedChange;
          realNode.addEventListener("change", wrappedChange);
        } else {
          updated.AttachedOnChange = oldTree.AttachedOnChange;
        }

        return realNode;
      }
    } else {
      // Shouldn't happen in practice (every previously-rendered tree has a
      // real node by the time a second render diffs against it) — treated
      // as "nothing to reuse" rather than a crash, same defensive spirit
      // as Component's own IsMounted guard.
      Element created = Materialize(updated, parent);
      parent.appendChild(created);
      return created;
    }
  } else {
    Element created = Materialize(updated, parent);
    parent.appendChild(created);
    return created;
  }
}

// Releases whatever was previously mounted in a slot (calling its
// Teardown, which — for a Component — cascades into anything IT mounted
// too, however many levels deep, before its own app-facing OnUnmount runs)
// and removes its real node from the DOM, before a different Mountable (or
// plain content) takes over that slot. A no-op when there was nothing
// mounted here before — the common "first render of this slot" case.
void UnmountPrevious(Element parent, VElement? old, Mountable? oldMounted) {
  // Teardown() only applies when there really was a Mounted instance here
  // before (nothing to tear down for plain content) — but the real node
  // itself needs removing whenever `old` had one, Mounted or not: a plain
  // VElement's slot turning into a Mounted one is exactly as much a
  // wholesale replacement as the reverse direction (handled by falling
  // through to Materialize below in Patch), and both need the OLD node
  // gone before the NEW one is appended, not left behind as a stray
  // sibling.
  if (oldMounted != null) {
    Mountable m = oldMounted;
    m.Teardown();
  }
  if (old != null) {
    VElement oldTree = old;
    Element? maybeOldNode = oldTree.RealNode;
    if (maybeOldNode != null) {
      Element oldNode = maybeOldNode;
      parent.removeChild(oldNode);
    }
  }
}

// Keyed reconciliation: each VElement's own Id is its key when non-empty —
// a real, existing DOM convention, needing no new API or syntax. A new
// child whose Id matches an old child's Id is patched against that old
// child (reusing its real node) regardless of position; a new child with
// no Id, or an Id not present among the old children, falls back to
// pairing positionally against whatever old children are still unconsumed,
// in order. DOCUMENTED, REAL LIMITATION: without stable Ids, a reordered
// list still produces the correct final output, but a given item's real
// DOM node (and anything stateful attached to it, like focus) isn't
// guaranteed to follow its data across the reorder — give list items a
// stable Id for that guarantee.
void PatchChildren(Element parent, VElement[] oldChildren, VElement[] newChildren) {
  // Map, not a Push loop — Push is deliberately non-mutating (a real
  // spread-copy every call, see KopScript's own README), so building an
  // n-length array by Push-ing once per element in a loop is an
  // accidental O(n^2) on every single PatchChildren call, however small
  // the actual diff. Map is a real, single O(n) pass straight to
  // Array.prototype.map.
  bool[] oldConsumed = oldChildren.Map((VElement c) => false);
  number[] matchedOldIndex = newChildren.Map((VElement c) => -1);

  // Pass 1: keyed matches, by Id.
  for (number i = 0; i < newChildren.Length; i = i + 1) {
    if (newChildren[i].Id.Length == 0) { continue; }
    for (number j = 0; j < oldChildren.Length; j = j + 1) {
      if (!oldConsumed[j] && oldChildren[j].Id == newChildren[i].Id) {
        matchedOldIndex[i] = j;
        oldConsumed[j] = true;
        break;
      }
    }
  }

  // Pass 2: positional fallback for everything Pass 1 didn't match —
  // pair each remaining new child against the next still-unconsumed old
  // child, in order.
  number nextOld = 0;
  for (number i = 0; i < newChildren.Length; i = i + 1) {
    if (matchedOldIndex[i] >= 0) { continue; }
    while (nextOld < oldChildren.Length && oldConsumed[nextOld]) {
      nextOld = nextOld + 1;
    }
    if (nextOld < oldChildren.Length) {
      matchedOldIndex[i] = nextOld;
      oldConsumed[nextOld] = true;
      nextOld = nextOld + 1;
    }
  }

  // Whether anything is actually moving at all — same length, and every
  // new position matched the *same* old position. The extremely common
  // case for a list that's only had some of its rows' own content change
  // (e.g. "update every 10th row"), where reconciliation still has real
  // work to do (see Pass 1/2 above and Patch() itself) but nothing needs
  // to physically move in the DOM at all.
  bool needsReorder = oldChildren.Length != newChildren.Length;
  for (number i = 0; i < newChildren.Length; i = i + 1) {
    if (matchedOldIndex[i] != i) {
      needsReorder = true;
      break;
    }
  }

  // Pass 3: patch/create each new child in order, then — only if the list
  // actually needs reordering — move it into its correct final position.
  // appendChild on a node already attached elsewhere in the DOM MOVES it
  // (real DOM semantics), so processing new children in their final
  // desired order and always appending naturally builds up the correct
  // sequence, no separate insertBefore/reference-node bookkeeping needed.
  // Safe because VElement.Children is always the COMPLETE list of a
  // node's children — nothing else ever shares `parent`. Skipping the
  // move entirely when `needsReorder` is false avoids a real DOM API call
  // per child for the common no-reorder case — Patch() itself already
  // updates or replaces a reused/changed node exactly in place either way.
  for (number i = 0; i < newChildren.Length; i = i + 1) {
    VElement? matchedOld = null;
    if (matchedOldIndex[i] >= 0) {
      matchedOld = oldChildren[matchedOldIndex[i]];
    }
    Element childNode = Patch(parent, matchedOld, newChildren[i]);
    if (needsReorder) {
      parent.appendChild(childNode);
    }
  }

  // Pass 4: remove whatever old children never got reused — releasing
  // anything Mounted first (see velement.ks) so a child genuinely dropped
  // from a list (not replaced in the same slot by a different one, which
  // Patch() itself already handles via UnmountPrevious — removed outright)
  // still gets torn down. This is the one place THAT case is reachable
  // from, since Patch() only ever sees one slot at a time, never a slot
  // disappearing entirely.
  for (number j = 0; j < oldChildren.Length; j = j + 1) {
    if (oldConsumed[j]) { continue; }
    Mountable? maybeOldMounted = oldChildren[j].Mounted;
    if (maybeOldMounted != null) {
      Mountable m = maybeOldMounted;
      m.Teardown();
    }
    Element? maybeOldNode = oldChildren[j].RealNode;
    if (maybeOldNode != null) {
      Element oldNode = maybeOldNode;
      parent.removeChild(oldNode);
    }
  }
}

// Recursively finds and tears down every Mounted node within `tree` (tree
// itself included) — used by Component's own Teardown (see component.ks)
// so removing ONE component transitively releases everything IT rendered,
// however many Mounted levels deep, not only the outermost one. A pure
// data walk over the retained VElement tree; never touches the real DOM
// itself (the caller already owns removing whatever real node is actually
// attached).
void UnmountTree(VElement tree) {
  Mountable? maybeMounted = tree.Mounted;
  if (maybeMounted != null) {
    Mountable m = maybeMounted;
    m.Teardown();
  }
  foreach (VElement child in tree.Children) {
    UnmountTree(child);
  }
}
